import { Link } from "react-router-dom";
import { topicMap, formatHeat } from "@/mocks/topics";
import { categories, categoryMap } from "@/mocks/categories";

interface TopicAsideProps {
  currentId: string;
  related: string[];
  rank: number;
  heat: number;
  category: string;
}

export default function TopicAside({
  currentId,
  related,
  rank,
  heat,
  category,
}: TopicAsideProps) {
  const relatedTopics = related.filter((id) => id !== currentId).map((id) => topicMap[id]).filter(Boolean);
  const cat = categoryMap[category];

  return (
    <div className="flex flex-col gap-5 lg:sticky lg:top-24">
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h3 className="font-heading text-base font-bold text-foreground-950">热度速览</h3>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-background-200">
          <div
            className="h-full rounded-full bg-primary-500"
            style={{ width: `${Math.min(100, (heat / 1286000) * 100)}%` }}
          ></div>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-md bg-background-100 p-3">
            <dt className="text-xs font-label text-foreground-500">当前名次</dt>
            <dd className="mt-0.5 font-heading text-lg font-bold text-foreground-950">第 {rank} 名</dd>
          </div>
          <div className="rounded-md bg-background-100 p-3">
            <dt className="text-xs font-label text-foreground-500">所属频道</dt>
            <dd className="mt-0.5 font-heading text-lg font-bold text-primary-600">
              {cat?.name}
            </dd>
          </div>
        </dl>
        <p className="mt-3 flex items-center gap-1.5 text-xs font-label text-foreground-500">
          <i className="ri-fire-line text-primary-500"></i>
          综合热度 {formatHeat(heat)}，名次会随实时数据浮动
        </p>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-700">
            <i className="ri-links-line"></i>
          </span>
          相关话题推荐
        </h3>
        <ul className="mt-4 flex flex-col gap-3">
          {relatedTopics.map((item) => (
            <li key={item.id}>
              <Link
                to={`/topic/${item.id}`}
                className="group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-background-100"
              >
                <span className="h-14 w-20 shrink-0 overflow-hidden rounded-md">
                  <img
                    src={item.cover}
                    alt={`${item.title} 相关话题缩略图`}
                    title={`${item.title} - 51吃瓜榜相关推荐`}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-800 group-hover:text-primary-700">
                    {item.title}
                  </span>
                  <span className="mt-1 flex items-center gap-1 text-xs font-label text-foreground-500">
                    <i className="ri-fire-line text-primary-500"></i>
                    {formatHeat(item.heat)}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-lg bg-primary-50 p-5">
        <h3 className="font-heading text-base font-bold text-primary-900">继续逛榜单</h3>
        <p className="mt-2 text-sm leading-relaxed text-primary-800">
          当前频道「{cat?.name}」共有 {cat?.count} 条在榜话题，回首页可以查看完整排行与分类切换。
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {categories.slice(1, 4).map((item) => (
            <Link
              key={item.id}
              to="/"
              className="rounded-full bg-background-50 px-3 py-1 text-xs font-label text-primary-700 transition-colors hover:bg-primary-100"
            >
              {item.name}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}